'use client';

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent, PointerEvent } from 'react';
import * as Dialog from './design-dialog';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, X } from '@phosphor-icons/react';
import { buildCampaigns, coverSource, type Campaign, type CoverFormat } from './campaign-model';
import type { PublicPromotion } from '@/lib/api/content';
import type { Banner } from '@/lib/api/admin-banners';
import { useMemo } from 'react';

const addTo = (src: string) => (set: ReadonlySet<string>) =>
  set.has(src) ? set : new Set(set).add(src);

function CoverArtwork({
  campaign,
  failed,
  onError,
  mobile = false,
  priority = false,
}: {
  campaign: Campaign;
  failed: ReadonlySet<string>;
  onError: (src: string) => void;
  mobile?: boolean;
  priority?: boolean;
}) {
  const desktopSrc = coverSource(campaign, 'desktop', failed);
  const mobileSrc = coverSource(campaign, 'mobile', failed);
  return (
    <div
      className={`cover-artwork cover-artwork-${campaign.key}${mobile ? ' cover-artwork-mobile' : ''}`}
      style={
        {
          '--cover-mobile-photo': `url("${mobileSrc}")`,
          '--cover-desktop-photo': `url("${desktopSrc}")`,
        } as CSSProperties
      }
      aria-hidden="true"
    >
      <div className="cover-parallax">
        <picture>
          {!mobile && <source media="(max-width: 650px)" srcSet={mobileSrc} />}
          <img
            className="cover-image"
            src={mobile ? mobileSrc : desktopSrc}
            width={mobile ? 1080 : 1920}
            height={mobile ? 1350 : 823}
            alt=""
            decoding="async"
            loading={priority ? 'eager' : undefined}
            fetchPriority={priority ? 'high' : 'auto'}
            onError={(event) => onError(event.currentTarget.currentSrc)}
          />
        </picture>
      </div>
    </div>
  );
}

export function CampaignCovers({
  onChoose,
  promotions,
  banners,
}: {
  onChoose: () => void;
  promotions: PublicPromotion[];
  banners: Banner[];
}) {
  const campaigns = useMemo(() => buildCampaigns(promotions, banners), [promotions, banners]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selected, setSelected] = useState<Campaign | null>(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [paused, setPaused] = useState(false);
  const [inView, setInView] = useState(true);
  const [pageVisible, setPageVisible] = useState(true);
  const [format, setFormat] = useState<CoverFormat | null>(null);
  // Images that finished loading or failed, and the failed subset: a failure must
  // never stall autoplay, and failed uploaded art falls back to the local asset.
  const [settled, setSettled] = useState<ReadonlySet<string>>(() => new Set());
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set());
  const stageRef = useRef<HTMLDivElement>(null);
  const termsRef = useRef<HTMLButtonElement | null>(null);
  const pointerStart = useRef<{ id: number; x: number; y: number } | null>(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  const current = campaigns[activeIndex % (campaigns.length || 1)];
  const next = campaigns.length > 1 ? campaigns[(activeIndex + 1) % campaigns.length] : undefined;
  const currentSrc = current && format ? coverSource(current, format, failed) : '';
  const nextSrc = next && format ? coverSource(next, format, failed) : '';
  const offer = selected?.offer;
  const markFailed = (src: string) => {
    if (!src) return;
    setSettled(addTo(src));
    setFailed(addTo(src));
  };

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(media.matches);
    media.addEventListener('change', update);
    update();
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 650px)');
    const update = () => setFormat(media.matches ? 'mobile' : 'desktop');
    media.addEventListener('change', update);
    update();
    return () => media.removeEventListener('change', update);
  }, []);

  // The visible slide loads first, then only the one after it.
  useEffect(() => {
    const src = [currentSrc, nextSrc].find((candidate) => candidate && !settled.has(candidate));
    if (!src) return;
    let disposed = false;
    const finish = (loaded: boolean) => {
      if (disposed) return;
      setSettled(addTo(src));
      if (!loaded) setFailed(addTo(src));
    };
    const image = new Image();
    image.onload = () => finish(true);
    image.onerror = () => finish(false);
    image.src = src;
    return () => {
      disposed = true;
    };
  }, [currentSrc, nextSrc, settled]);

  useEffect(() => {
    const visibility = () => setPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', visibility);
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting && entry.intersectionRatio >= 0.2),
      { threshold: [0, 0.2] }
    );
    if (stageRef.current) observer.observe(stageRef.current);
    return () => {
      document.removeEventListener('visibilitychange', visibility);
      observer.disconnect();
    };
  }, []);

  const isPaused =
    paused ||
    hovered ||
    focused ||
    dragging ||
    !inView ||
    !pageVisible ||
    !!selected ||
    !!reducedMotion ||
    !settled.has(currentSrc);
  useEffect(() => {
    if (isPaused || campaigns.length < 2) return;
    const timer = window.setTimeout(
      () => setActiveIndex((index) => (index + 1) % campaigns.length),
      6000
    );
    return () => window.clearTimeout(timer);
  }, [activeIndex, isPaused, campaigns.length]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || reducedMotion || window.matchMedia('(pointer: coarse)').matches) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const bounds = stage.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, -bounds.top / bounds.height));
      stage.style.setProperty('--cover-scroll', `${progress * 9}px`);
    };
    const scroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', scroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', scroll);
      cancelAnimationFrame(frame);
      stage.style.removeProperty('--cover-scroll');
    };
  }, [reducedMotion]);

  const choose = (index: number) => setActiveIndex((index + campaigns.length) % campaigns.length);
  const onCarouselKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    let next: number;
    if (event.key === 'ArrowRight') next = (activeIndex + 1) % campaigns.length;
    else if (event.key === 'ArrowLeft')
      next = (activeIndex + campaigns.length - 1) % campaigns.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = campaigns.length - 1;
    else if (event.key === ' ') {
      event.preventDefault();
      setPaused(!isPaused);
      if (isPaused) {
        setFocused(false);
        setHovered(false);
      }
      return;
    } else return;
    event.preventDefault();
    choose(next);
  };
  const tilt = (event: PointerEvent<HTMLDivElement>) => {
    if (reducedMotion || event.pointerType !== 'mouse') return;
    if (pointerStart.current) return;
    const box = event.currentTarget.getBoundingClientRect();
    const x = Math.max(-1, Math.min(1, ((event.clientX - box.left) / box.width) * 2 - 1));
    const y = Math.max(-1, Math.min(1, ((event.clientY - box.top) / box.height) * 2 - 1));
    const style = event.currentTarget.style;
    style.setProperty('--cover-x', `${x * 4}px`);
    style.setProperty('--cover-y', `${y * 3}px`);
  };
  const resetTilt = () => {
    const style = stageRef.current?.style;
    ['--cover-x', '--cover-y'].forEach((name) => style?.removeProperty(name));
  };

  const endSwipe = (event: PointerEvent<HTMLDivElement>, cancelled = false) => {
    const start = pointerStart.current;
    pointerStart.current = null;
    setDragging(false);
    event.currentTarget.style.removeProperty('--swipe-x');
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    if (!start || cancelled || event.pointerId !== start.id) return;
    const dx = event.clientX - start.x,
      dy = event.clientY - start.y;
    if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.2)
      choose(activeIndex + (dx < 0 ? 1 : -1));
  };

  if (!current)
    return (
      <section className="cover-empty" aria-label="Азиаточка">
        <h1>Роллы. Любовь. Точка.</h1>
      </section>
    );
  return (
    <>
      <section className="cover-hero" id="stories" aria-label="Акции Азиаточки">
        <div
          className={`cover-stage${dragging ? ' is-dragging' : ''}${current.desktop ? ' cover-managed' : ''}`}
          ref={stageRef}
          role="group"
          aria-roledescription="карусель"
          aria-label="Акции Азиаточки"
          aria-describedby="cover-instructions"
          data-autoplay={isPaused ? 'paused' : 'playing'}
          tabIndex={0}
          onKeyDown={onCarouselKey}
          onFocusCapture={(event) => {
            if ((event.target as HTMLElement).matches(':focus-visible')) setFocused(true);
          }}
          onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null))
              setFocused(false);
          }}
          onPointerEnter={(event) => {
            if (event.pointerType === 'mouse') setHovered(true);
          }}
          onPointerLeave={(event) => {
            if (event.pointerType === 'mouse') setHovered(false);
            resetTilt();
          }}
          onPointerMove={(event) => {
            const start = pointerStart.current;
            if (start && !reducedMotion) {
              const dx = event.clientX - start.x,
                dy = event.clientY - start.y;
              if (Math.abs(dx) > Math.abs(dy))
                event.currentTarget.style.setProperty(
                  '--swipe-x',
                  `${Math.max(-28, Math.min(28, dx * 0.12))}px`
                );
            } else tilt(event);
          }}
          onPointerDown={(event) => {
            if (event.button !== 0 || (event.target as Element).closest('button, a')) return;
            setFocused(false);
            pointerStart.current = { id: event.pointerId, x: event.clientX, y: event.clientY };
            event.currentTarget.setPointerCapture(event.pointerId);
            setDragging(true);
          }}
          onPointerUp={(event) => endSwipe(event)}
          onPointerCancel={(event) => endSwipe(event, true)}
        >
          <span className="cover-accessible-title" id="cover-instructions">
            Листайте свайпом или стрелками влево и вправо. Автосмена каждые 6 секунд. Пробел — пауза
            или продолжение.
          </span>
          <div
            className="cover-panel"
            role="group"
            aria-roledescription="слайд"
            aria-label={`${activeIndex + 1} из ${campaigns.length}: ${current.title}`}
            aria-live={isPaused ? 'polite' : 'off'}
          >
            <AnimatePresence initial={false}>
              <motion.div
                className="cover-slide"
                key={current.slug}
                initial={{ opacity: 0, scale: reducedMotion ? 1 : 1.025 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: reducedMotion ? 0 : 0.45, ease: [0.22, 1, 0.36, 1] }}
                aria-hidden="true"
              >
                <CoverArtwork
                  campaign={current}
                  failed={failed}
                  onError={markFailed}
                  priority={current === campaigns[0]}
                />
              </motion.div>
            </AnimatePresence>
            {!current.desktop && (
              <div
                className={`cover-caption cover-caption-${current.key}${current.customHeading ? ' cover-caption-custom' : ''}`}
              >
                <span className="cover-eyebrow">{current.eyebrow}</span>
                <h2 className="cover-heading">
                  {current.heading.map((line) => (
                    <span key={line}>{line}</span>
                  ))}
                </h2>
                <p className="cover-note">{current.note}</p>
              </div>
            )}
            {current.offer && (
              <button
                className="cover-terms"
                onClick={(event) => {
                  termsRef.current = event.currentTarget;
                  setSelected(current);
                }}
              >
                Условия акции
              </button>
            )}
            {current.link && (
              <a className="cover-terms" href={current.link}>
                Подробнее
              </a>
            )}
          </div>
        </div>
      </section>
      <Dialog.Root
        open={!!selected}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="dialog-overlay" />
          <Dialog.Content
            className="campaign-dialog cover-terms-dialog"
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              termsRef.current?.focus();
            }}
          >
            <Dialog.Close className="dialog-close" aria-label="Закрыть условия акции">
              <X size={24} />
            </Dialog.Close>
            {selected && offer && (
              <>
                <div className="cover-dialog-image">
                  <CoverArtwork campaign={selected} failed={failed} onError={markFailed} mobile />
                </div>
                <div className="campaign-terms">
                  <span className="detail-eyebrow">Акции Азиаточки</span>
                  <Dialog.Title>{offer.title}</Dialog.Title>
                  <Dialog.Description>{offer.description}</Dialog.Description>
                  <h3>Условия акции</h3>
                  <ul>
                    {offer.terms.map((term) => (
                      <li key={term}>{term}</li>
                    ))}
                  </ul>
                  {offer.legal_note && <p className="campaign-legal">{offer.legal_note}</p>}
                  <button
                    className="primary-button"
                    onClick={() => {
                      setSelected(null);
                      onChoose();
                    }}
                  >
                    Выбрать роллы <ArrowRight size={18} />
                  </button>
                </div>
              </>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
